import { apiFetch } from './client';
import type { Venta } from './tienda.api';

/**
 * COTIZACIONES (`/api/facturacion/cotizaciones`) — un precio por escrito, con su número propio.
 * No descuenta existencia. Al convertirla nace una venta en borrador con esos precios.
 */

export type EstadoDeCotizacion = 'ABIERTA' | 'VENCIDA' | 'CONVERTIDA' | 'ANULADA';

export interface LineaCotizada {
  productVariantId: string;
  sku: string | null;
  descripcion: string;
  talla: string;
  cantidad: number;
  precio: number;
  descuento: number;
  importe: number;
}

interface VentaDeLaCotizacion {
  id: string;
  numero: string;
  estado: 'BORRADOR' | 'CONFIRMADA' | 'ANULADA';
}

export interface CotizacionEnLista {
  id: string;
  numero: string;
  /** Como se enseña: una abierta con el día pasado sale «vencida». */
  estadoALaVista: EstadoDeCotizacion;
  clienteId: string | null;
  clienteNombre: string;
  total: number;
  piezas: number;
  validaHasta: string | null;
  createdAt: string;
  venta: VentaDeLaCotizacion | null;
}

export interface Cotizacion extends Omit<CotizacionEnLista, 'piezas'> {
  clienteTelefono: string | null;
  lineas: LineaCotizada[];
  subtotal: number;
  descuento: number;
  envio: number;
  notas: string | null;
  emisor: { nombre: string; rnc: string; direccion: string; telefono: string };
}

const BASE = '/api/facturacion/cotizaciones';

export const listarCotizaciones = (): Promise<CotizacionEnLista[]> => apiFetch<CotizacionEnLista[]>(BASE);

export const leerCotizacion = (id: string): Promise<Cotizacion> => apiFetch<Cotizacion>(`${BASE}/${id}`);

export const crearCotizacion = (body: {
  lineas: Array<{ productVariantId: string; cantidad: number; unitPrice?: number; descuento?: number }>;
  clienteId?: string | null;
  clienteNombre?: string | null;
  clienteTelefono?: string | null;
  descuento?: number;
  envio?: number;
  /** `YYYY-MM-DD`. */
  validaHasta?: string | null;
  notas?: string | null;
}): Promise<Cotizacion> => apiFetch<Cotizacion>(BASE, { method: 'POST', body });

export const convertirEnVenta = (id: string): Promise<{ cotizacion: Cotizacion; venta: Venta }> =>
  apiFetch(`${BASE}/${id}/convertir`, { method: 'POST', body: {} });

export const anularCotizacion = (id: string): Promise<Cotizacion> =>
  apiFetch<Cotizacion>(`${BASE}/${id}/anular`, { method: 'POST', body: {} });
